// Quote Builder — picker selection pubsub.
//
// Mirrors source `UI.activePicker` (kos-qb-v3.js renderPicker block at
// line 2353). The Products panel on the LEFT writes which canonical
// category is currently selected; the picker area on the RIGHT reads
// that selection and mounts the matching picker (door / cabinet / handle
// / etc).
//
// Why a separate module: the left and right panes live in different
// DOM subtrees with their own mount lifecycles, and the active picker
// also needs to survive across re-renders of either pane.

import type { ItemCategory, W8Cab } from "./types";

export type ActivePicker = ItemCategory | null;

let active: ActivePicker = null;
const listeners = new Set<(cat: ActivePicker, force: boolean) => void>();

// ── Selected cab pubsub (W8 picker, Phase 1) ─────────────────────────────
//
// Orthogonal to setActivePicker — the W8 cabinet picker needs the full
// DTN cab record (code, desc, unit_label, pg1..pg5 etc.), not just a
// category. Left-pane W8 list publishes a click via setSelectedCab(cab);
// picker-area subscribes and re-mounts the W8 configurator with the
// payload. Cleared with setSelectedCab(null) on Cancel.
let selectedCab: W8Cab | null = null;
const cabListeners = new Set<(cab: W8Cab | null) => void>();

/** One-shot pre-fill payload consumed by the next picker mount.
 *  Cleared after the picker reads it. Used when the user clicks a
 *  product card in the left pane and the right pane picker should
 *  open pre-populated with that product's data. */
let pendingSeed: Record<string, unknown> | null = null;

export function setPickerSeed(seed: Record<string, unknown> | null): void {
  pendingSeed = seed;
}

export function consumePickerSeed(): Record<string, unknown> | null {
  const s = pendingSeed;
  pendingSeed = null;
  return s;
}

// ── Door-picker supplier context ─────────────────────────────────────────
//
// The door picker (door-picker.ts) is shared across catalogue-backed
// suppliers. Unlike the one-shot seed, the supplier persists across the
// whole door-picking journey (the picker also mounts seedless when the user
// first opens the Doors view, before clicking a range card), so it lives
// here as durable state rather than on the seed. The left pane sets it before
// activating the doors picker; the picker reads it at mount.
//
// `slug`  → API supplier param + discount/drilling cache key ("tkc", "pws").
// `label` → quote-line supplier string + breadcrumb (must match the relevant
//            PICKER_SUPPLIERS key so pricing resolves — "TKC", "PWS").
//
// Defaults to TKC so any caller that doesn't set it (and all existing TKC
// flows) behave exactly as before.
export interface DoorSupplierCtx { slug: string; label: string; }

let doorSupplier: DoorSupplierCtx = { slug: "tkc", label: "TKC" };

export function setDoorSupplier(ctx: DoorSupplierCtx): void {
  doorSupplier = { slug: ctx.slug, label: ctx.label };
}

export function getDoorSupplier(): DoorSupplierCtx {
  return { ...doorSupplier };
}

export function getActivePicker(): ActivePicker {
  return active;
}

export function setActivePicker(cat: ActivePicker): void {
  if (active === cat) return;
  active = cat;
  for (const fn of listeners) {
    try { fn(active, false); } catch { /* swallow */ }
  }
}

// Force re-mount of the current picker even when the category hasn't
// changed. Mirrors source `rerenderPicker()` (kos-qb-v3.js _qbv3ProdAdd
// path) which unconditionally re-renders the picker after seeding it
// from a left-pane click, so the pending seed gets consumed.
export function notifyPickerRemount(): void {
  for (const fn of listeners) {
    try { fn(active, true); } catch { /* swallow */ }
  }
}

export function subscribePicker(
  fn: (cat: ActivePicker, force: boolean) => void,
): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ── Selected SJB cabinet pubsub ──────────────────────────────────────────
//
// Orthogonal to setActivePicker for the same reason `selectedCab` is: an SJB
// cabinet is still ItemCategory "cabinets", but which SJB SKU is being
// configured cannot be expressed in that union. Widening ActivePicker would
// have made every consumer of the type handle a value that is not an item
// category. picker-area reads this inside its `cabinets` branch and prefers
// the SJB picker when a code is set.

let selectedSjbCode: string | null = null;
const sjbListeners = new Set<(code: string | null) => void>();

export function setSelectedSjbCode(code: string | null): void {
  selectedSjbCode = code;
  for (const fn of sjbListeners) {
    try { fn(selectedSjbCode); } catch { /* swallow */ }
  }
}

export function getSelectedSjbCode(): string | null {
  return selectedSjbCode;
}

export function subscribeSelectedSjb(fn: (code: string | null) => void): () => void {
  sjbListeners.add(fn);
  return () => { sjbListeners.delete(fn); };
}

// ── SJB panel picker pubsub ──────────────────────────────────────────────
//
// Same shape, and the same reason, as `selectedSjbCode`: an SJB panel is
// still ItemCategory "panels", but "which panel flow" cannot be said in that
// union, and widening ActivePicker would force every consumer to handle a
// value that is not an item category. picker-area reads this inside its
// `panels` branch and prefers the SJB picker when it is set.
//
// A boolean rather than a code, because the SJB panel picker opens on no
// product at all — the retailer types a size and the catalogue answers.

let sjbPanelOpen = false;
const sjbPanelListeners = new Set<(open: boolean) => void>();

export function setSjbPanelOpen(open: boolean): void {
  sjbPanelOpen = open;
  for (const fn of sjbPanelListeners) {
    try { fn(sjbPanelOpen); } catch { /* swallow */ }
  }
}

export function getSjbPanelOpen(): boolean {
  return sjbPanelOpen;
}

export function subscribeSjbPanel(fn: (open: boolean) => void): () => void {
  sjbPanelListeners.add(fn);
  return () => { sjbPanelListeners.delete(fn); };
}

// ── Configurator in progress ─────────────────────────────────────────────
//
// Clicking a product in the left panel replaces whatever is open in the
// picker area — `setActivePicker` + `notifyPickerRemount` unmount the current
// picker and mount a new one. For a browse-and-pick surface that costs
// nothing. For a CONFIGURATOR it throws away work: a part-built SJB cabinet
// with its colour, hinge side, frontal and drilling all chosen disappears
// with one stray click, silently.
//
// That was already true; a cross-catalogue result list makes it far easier to
// hit, because the rows you did not expect are now in the same list.
//
// A picker that holds real state REGISTERS here while it is mounted, naming
// what would be lost, and the left panel asks before replacing it. Deliberately
// a label rather than a boolean: the confirm has to say WHICH thing is about
// to be discarded, or it is just another dialog to click through.
//
// Registration is cleared on unmount, which is also how it survives the
// confirm itself — the picker is only torn down once the retailer says yes.

let configuratorLabel: string | null = null;

/** Called by a configurator that would lose work if it were replaced.
 *  Pass null on unmount. */
export function setConfiguratorInProgress(label: string | null): void {
  configuratorLabel = label || null;
}

/** What is open and would be lost, or null. */
export function getConfiguratorInProgress(): string | null {
  return configuratorLabel;
}

// ── Selected cab pubsub ──────────────────────────────────────────────────

export function setSelectedCab(cab: W8Cab | null): void {
  selectedCab = cab;
  for (const fn of cabListeners) {
    try { fn(selectedCab); } catch { /* swallow */ }
  }
}

export function getSelectedCab(): W8Cab | null {
  return selectedCab;
}

export function subscribeSelectedCab(fn: (cab: W8Cab | null) => void): () => void {
  cabListeners.add(fn);
  return () => { cabListeners.delete(fn); };
}
